import Skeleton from "@/v2-ui/skeleton"
import Tooltip from "@/v2-ui/tooltip"
import Dot, { DotText } from "@/v2-backoffice-client/components/dot"
import { useTabParam } from "@/v2-backoffice-client/components/tabs"
import {
  LAYER_VIEW_DEFAULT_TAB,
  LAYER_VIEW_TABS_ID,
} from "@/v2-backoffice-client/layer/layer.constants"
import {
  useLayerVisibilityGates,
  type LayerVisibilityLayer,
} from "@/v2-backoffice-client/layer/visibility/layer.visibility.hooks"

export type LayerVisibilityDotProps = {
  layer: LayerVisibilityLayer
}

/**
 * The layer sheet's "visible to customers?" as one info-grid value: a
 * green "Yes, in Norway", an amber "Yes, but unfiled", or a red "No" naming
 * the gate that hides it. The five gates sit in the tooltip; when one can
 * be fixed in the sheet, the value is a button that opens the Availability
 * tab, which lays the same gates out in the same order.
 *
 * A failed read is "Unknown", never "No": not being able to ask is not the
 * same as the layer being hidden.
 */
function LayerVisibilityDot(props: LayerVisibilityDotProps) {
  const { layer } = props

  const [, setTab] = useTabParam(LAYER_VIEW_TABS_ID, LAYER_VIEW_DEFAULT_TAB)
  const {
    isLoading,
    error,
    hasRuntimeRow,
    isVisible,
    countryNames,
    gates,
    blockers,
    degradations,
  } = useLayerVisibilityGates({ layer })

  if (isLoading && !hasRuntimeRow) {
    return <Skeleton className="h-4 w-28" aria-busy="true" />
  }

  if (!hasRuntimeRow) {
    return (
      <Tooltip>
        <Tooltip.Trigger asChild>
          <span className="inline-flex cursor-help">
            <DotText variant={error ? "muted" : "destructive"}>
              {error ? "Unknown" : "No"}
            </DotText>
          </span>
        </Tooltip.Trigger>
        <Tooltip.Content side="bottom">
          <span className="flex max-w-xs flex-col gap-1">
            {error ? (
              <>
                <span className="font-medium">
                  Could not read the visibility gates.
                </span>
                <span className="opacity-70">{error.message}</span>
              </>
            ) : (
              <>
                Not in this environment&apos;s runtime — a catalog publish seeds
                it.
              </>
            )}
          </span>
        </Tooltip.Content>
      </Tooltip>
    )
  }

  const fixTab = [...blockers, ...degradations].find((gate) => gate.tab)?.tab
  const summary = isVisible
    ? degradations.length > 0
      ? `Yes, but ${degradations.map((gate) => gate.label.toLowerCase()).join(" and ")}: no`
      : `Yes, in ${countryNames.join(", ")}`
    : `No — ${blockers.map((gate) => gate.label.toLowerCase()).join(", ")}`
  const variant = isVisible
    ? degradations.length > 0
      ? "warning"
      : "success-1"
    : "destructive"

  const value = <DotText variant={variant}>{summary}</DotText>

  return (
    <Tooltip>
      <Tooltip.Trigger asChild>
        {fixTab ? (
          <button
            type="button"
            className="info-grid-item-value ui-link"
            data-feature-code="UI_BackofficeLayers_Visibility_Fix"
            onClick={() => setTab(fixTab)}
          >
            {value}
          </button>
        ) : (
          <span className="inline-flex cursor-help">{value}</span>
        )}
      </Tooltip.Trigger>
      {/* Tooltips are inverted (bg-foreground / text-background), so the
          gate lines are plain spans inheriting that colour — DotText's own
          muted text would vanish against it. */}
      <Tooltip.Content side="bottom">
        <span className="flex max-w-sm flex-col gap-1">
          <span className="font-medium">
            {isVisible
              ? `Visible to customers in ${countryNames.join(", ")}`
              : "Hidden from customers"}
          </span>
          {gates.map((gate) => (
            <span key={gate.key} className="flex items-center gap-1.5">
              <Dot
                variant={
                  gate.isOk
                    ? "success-1"
                    : gate.isBlocking
                      ? "destructive"
                      : "warning"
                }
              />
              <span>
                {gate.label}: {gate.detail}
              </span>
            </span>
          ))}
          {fixTab && (
            <span className="opacity-70">
              Click to open the Availability tab, where every gate is set.
            </span>
          )}
        </span>
      </Tooltip.Content>
    </Tooltip>
  )
}

export default LayerVisibilityDot
